﻿import {computed, defineComponent, onUnmounted, type PropType, ref, toRefs, watch} from 'vue';
import {useFieldArray, useForm} from 'vee-validate';
import * as yup from 'yup';
import {availableCountries} from "@/app/shared/helpers/country.ts";
import {useLocationStore} from "@/app/stores/location-store.ts";
import { checkVAT, countries } from 'jsvat';
import {AthumiService} from "@/api/enterprise-gateway-api.proxy.ts";
import {AutoComplete, Button, InputText, Select} from 'primevue';
import {useUserStore} from "@/app/stores/user-store.ts";

export interface IBillingAddress {
    street: string;
    houseNumber: string;
    boxNumber?: string;
    postalCode: string;
    city: string;
    country: string;
    emails: { service: string, email: string }[]
    showEmail: boolean;
    showVatNumber: boolean;
    vatNumber?: string;
    identifier?: string;
    isCitizen: boolean;
    fullName?: string;
}

export const errorMessage = 'Het opgegeven btw-nummer is ongeldig. Contacteer accounting@athumi.eu indien u toch facturen op dit btw-nummer wenst.';

export const addressValidationSchema = yup.object({
    street: yup.string().required('Straat is verplicht'),
    houseNumber: yup.number().typeError('Voer een geldig huisnummer in').required('Huisnummer is verplicht'),
    boxNumber: yup.string().nullable(),
    postalCode: yup.string().required('Postcode is verplicht'),
    city: yup.string().required('Gemeente is verplicht'),
    country: yup.string().default('België').required('Land is verplicht'),
    emails: yup.array().of(
        yup.object({
            service: yup.string().required(),
            email: yup.string().email('Ongeldig e-mailadres').matches(/^[^\s@]+@[^\s@]+\.[^\s@]+$/, 'Ongeldig e-mailadres').when('$showEmail', {
                is: true,
                then: (schema) => schema.required('E-mailadres is verplicht'),
                otherwise: (schema) => schema.nullable()
            }),
        })
    ).nullable(),
    vatNumber: yup.string().when('showVatNumber', {
        is: true,
        then: (schema) => schema
            .required('BTW-nummer is verplicht')
            .test('vat-number', errorMessage, (value) => {
                const vatCheckResult = checkVAT(value, countries);
                return vatCheckResult.isValid;
            }),
        otherwise: (schema) => schema.nullable()
    }),
    showEmail: yup.boolean(),
    showVatNumber: yup.boolean()
});

const streetMinLength: number = 2;

export default defineComponent({
    name: 'AegAddressForm',
    components: { InputText, Button, Select, AutoComplete },
    props: {
        address: {
            type: Object as PropType<IBillingAddress>,
            required: true,
        },
        readOnly: {
            type: Boolean,
            required: false,
            default: true,
        },
        annotation: {
            type: String,
            required: false
        },
        service: {
            type: String as PropType<AthumiService>,
            required: false,
            default: AthumiService.ELys
        },
        companyName: {
            type: String,
            default: undefined,
        },
        identifier: {
            type: String,
            default: undefined,
        },

    },
    emits: ['update:address', 'validated'],
    setup(props, {emit}) {
        const locationStore = useLocationStore();
        const userStore = useUserStore();

        const hasAddress = computed((): boolean => {
            return Boolean(props.address.street && props.address.houseNumber && props.address.postalCode && props.address.city && props.address.country);
        });

        const showVatNumber = ref<boolean>(props.address.showVatNumber || false);
        const showEmail = ref<boolean>(props.address.showEmail);

        const {defineField, meta, values, setValues, errors, validate} = useForm<IBillingAddress>({
            validationSchema: addressValidationSchema,
            initialValues: {...props.address},
            validateOnMount: hasAddress.value ||showVatNumber.value,
        });

        const { fields: emails } = useFieldArray<{service: string, email: string}>('emails');

        const [street, streetProps] = defineField('street');
        const [houseNumber, houseNumberProps] = defineField('houseNumber');
        const [boxNumber, boxNumberProps] = defineField('boxNumber');
        const [postalCode, postalCodeProps] = defineField('postalCode');
        const [city, cityProps] = defineField('city');
        const [country, countryProps] = defineField('country');
        const [vatNumber, vatNumberProps] = defineField('vatNumber');

        const streetWrapper = computed({
            get() {
                return street.value;
            },
            set(newValue: string | {title: string, subtitle: string, value: string}) {
                if (!newValue) {
                    street.value = '';
                }
                else if (typeof newValue === 'object') {
                    // dropdown value selected
                    onStreetSelect(newValue.value);
                }
                else {
                    // input text
                    street.value = newValue;
                    if (autocompleteActive.value && newValue?.length >= streetMinLength) {
                        locationStore.fetchStreetSuggestions(newValue);
                    }
                }
            }
        });

        const houseNumberWrapper = computed({
            get() {
                return houseNumber.value;
            },
            set(newValue: string | {title: string, value: string}) {
                if (!newValue) {
                    houseNumber.value = '';
                }
                else if (typeof newValue === 'object') {
                    // dropdown value selected
                    onHouseNumberSelect(newValue.value);
                }
                else {
                    // input text
                    houseNumber.value = newValue;
                    if (autocompleteActive.value && street.value && city.value) {
                        locationStore.fetchHouseNumberSuggestions(street.value, city.value, newValue);
                    }
                }
            }
        });

        const autocompleteActive = computed(() => {
            return country.value && country.value.toLowerCase() === 'belgië';
        });

        const readOnly = ref<boolean>(props.readOnly);
        const isCitizen = ref<boolean>(props.address.isCitizen);
        const fullName = ref<string>(props.address.fullName || '');

        const toggleReadOnly = () => {
            readOnly.value = false;
        };

        const {streetData, streetDataFetching, houseNumberData, houseNumberDataFetching} = toRefs(locationStore);

        const onStreetSelect = async (streetSuggestion: string) => {
            const newAddress = locationStore.parseBillingAddress(streetSuggestion);
            if (!newAddress.street) {
                return;
            }
            street.value = newAddress.street;
            if (newAddress.houseNumber) {
                houseNumber.value = newAddress.houseNumber;
            } else {
                if (!houseNumber.value) {
                    focusStreetNumber();
                }
            }
            if (newAddress.boxNumber) {
                boxNumber.value = newAddress.boxNumber;
            }
            if (newAddress.postalCode) {
                postalCode.value = newAddress.postalCode;
            } else {
                if (newAddress.city && newAddress.street) {
                    postalCode.value = await locationStore.fetchPostalCodes(newAddress.street, newAddress.city) || '';
                }
            }
            if (newAddress.city) {
                city.value = newAddress.city;
            }
        }

        const onHouseNumberSelect = (houseNumberSuggestion: string) => {
            const newHouseNumberAndBox = locationStore.parseHouseNumberAndBoxNumber(houseNumberSuggestion);
            houseNumber.value = newHouseNumberAndBox.houseNumber;
            boxNumber.value = newHouseNumberAndBox.boxNumber;
        }

        const focusStreetNumber = () => {
            const element = document.querySelector('input[name="nummer"]') as HTMLInputElement | null;
            element?.focus();
        };

        const updateModelValue = (newVals: IBillingAddress) => {
            emit('update:address', {...newVals});
        };

        const annotation = computed((): string => {
            if (!!props.annotation) {
                return props.annotation;
            }

            if (userStore.user.isForeign ?? false) {
                return `Geef een algemeen e-mailadres op waarop uw organisatie de facturen en betaalbewijzen na opladen van saldo wenst te ontvangen.`;
            }

            return `Facturen worden via het Peppol-netwerk verzonden. Geef hier een algemeen e-mailadres op voor vragen over facturatie. Betaalbewijzen na opladen van saldo worden naar dit e-mailadres verzonden.`;
        });

        const multipleEmailsDescription = computed((): string => {
            if (userStore.user.isForeign ?? false) {
                return `Geef hier algemene e-mailadressen op waarop uw organisatie de facturen en betaalbewijzen na opladen van saldo wenst te ontvangen.`;
            }

            return `Facturen worden via het Peppol-netwerk verzonden. Geef hier algemene e-mailadressen op voor vragen over facturatie. Betaalbewijzen na opladen van saldo worden naar deze e-mailadressen verzonden.`;
        });

        watch(() => props.address, (newValue) => {
            // Only update if the values actually changed to avoid triggering Watcher 2 unnecessarily
            if (JSON.stringify(newValue) !== JSON.stringify(values)) {
                setValues({
                    ...newValue,
                }, false);
            }
            showVatNumber.value = newValue.showVatNumber;
            showEmail.value = newValue.showEmail;
            isCitizen.value = newValue.isCitizen;
            fullName.value = newValue.fullName || '';
            country.value = newValue.country || 'België';
        }, {deep: true});
        watch(() => props.readOnly, (newValue) => {
            readOnly.value = newValue;
            if (!newValue) {
                void validate();
            }
        });
        watch(() => ({...values}), updateModelValue, {deep: true});
        watch(() => meta.value.valid, () => {
            emit('validated', meta.value.valid)
        }, {deep: true});

        onUnmounted(() => {
            locationStore.reset();
        });

        return {
            countryOptions: availableCountries,
            streetWrapper,
            streetProps,
            streetData,
            streetDataFetching,
            houseNumberWrapper,
            houseNumberProps,
            houseNumberData,
            houseNumberDataFetching,
            boxNumber,
            boxNumberProps,
            postalCode,
            postalCodeProps,
            city,
            cityProps,
            country,
            countryProps,
            vatNumber,
            vatNumberProps,
            errors,
            readOnly,
            annotation,
            multipleEmailsDescription,
            isCitizen,
            fullName,
            showVatNumber,
            showEmail,
            emails,
            toggleReadOnly,
            onStreetDropdownClick: (event: any) => locationStore.fetchStreetSuggestions(event.query),
            onHouseNumberDropdownClick: (event: any) => locationStore.fetchHouseNumberSuggestions(street.value, city.value, event.query),
        }
    }
})
